import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../shared/material/material.module';
import { UserRef } from '../../core/generated';
import { MatDialog } from '@angular/material/dialog';
import { UserPreferencesCacheService } from '../../shared/services/user-preferences-cache.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ActivatedRoute, Router } from '@angular/router';
import { ContainerComponent } from '../../shared/container/container.component';
import { AuthCustomService } from '../../security/auth-custom.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isValidCPF } from '../../shared/utils/validators';
import { DialogTermsComponent } from '../../shared/dialogs/dialog-terms/dialog-terms.component';
import { DialogPrivacyPolicyComponent } from '../../shared/dialogs/dialog-privacy-policy/dialog-privacy-policy.component';
import { firstValueFrom } from 'rxjs';
import { EnumDescriptionPipe } from '../../shared/pipes/enum-description.pipe';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-user',
    standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, ContainerComponent, EnumDescriptionPipe, CommonModule],
    templateUrl: './user.component.html',
    styleUrl: './user.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UserComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private userPreferencesCacheService = inject(UserPreferencesCacheService);
  private deviceService = inject(DeviceDetectorService);
  private route = inject(ActivatedRoute);
  private authCustomService = inject(AuthCustomService);
  private snackBar = inject(MatSnackBar);
  private cdr = inject(ChangeDetectorRef);
  
  isMobile = this.deviceService.isMobile();
  userForm!: FormGroup;
  user?: UserRef;
  isLoading: boolean = true;
  isEditMode: boolean = false;
  isCpfRequired: boolean = false;
  originalCpf: string | null = null;

  ngOnInit() {
    this.initForm();
    this.loadUser();
  }

  private initForm() {
    this.userForm = this.fb.group({
      id: [{ value: '' }],
      email: [{ value: '', disabled: true }],
      firstName: [{ value: null, disabled: true }, Validators.required],
      lastName: [{ value: null, disabled: true }, Validators.required],
      cpf: [
        { value: null, disabled: true },
        [Validators.required, Validators.pattern(/^\d{11}$/), this.validateCPF],
      ],
      phoneNumber: [{ value: null, disabled: true }, Validators.required],
      registerDate: [{ value: '', disabled: true }],
      active: [{ value: true, disabled: true }],
      status: [{ value: '', disabled: true }],
      certifiedHubMedical: [{ value: false, disabled: true }],
      termsAccepted: [false],
    });
  }

  private async loadUser() {
    try {
      const resolved = this.route.snapshot?.data?.['userPreferences'];
      console.log('User preferences resolved:', resolved);
      const user = resolved?.user || await firstValueFrom(this.userPreferencesCacheService.getUser());
      
      if (!user) throw new Error('Usuário não encontrado');
      
      this.handleUserLoaded(user);
    } catch (error) {
      this.isLoading = false;
      this.snackBar.open('Erro ao carregar dados do usuário.', 'Fechar', { duration: 4000 });
      console.error('Erro ao carregar usuário:', error);
    }
  }

  private handleUserLoaded(user: UserRef) {
    this.user = user;
    this.isLoading = false;
    this.userForm.patchValue(user);
    this.originalCpf = user.cpf || null;
    this.isCpfRequired = !this.originalCpf || !isValidCPF(this.originalCpf);
    
    if (this.isCpfRequired) this.isEditMode = true;
    
    this.updateFormState();
    this.cdr.detectChanges();
  }

  toggleEditMode() {
    if (this.isCpfRequired) return; // Se é obrigatório, não pode sair do modo edição
    
    this.isEditMode = !this.isEditMode;
    this.updateFormState();
  }

  private updateFormState() {
    if (this.isEditMode) {
      if (!this.userForm.controls['certifiedHubMedical'].value) {
        this.userForm.controls['firstName'].enable();
        this.userForm.controls['lastName'].enable();
        this.userForm.controls['cpf'].enable();
      }
      this.userForm.controls['phoneNumber'].enable();
    } else {
      this.userForm.controls['firstName'].disable();
      this.userForm.controls['lastName'].disable();
      this.userForm.controls['cpf'].disable();
      this.userForm.controls['phoneNumber'].disable(); 
    }
  }

  async saveUser(): Promise<void> {
    if (!this.userForm.valid) {
      this.markFormGroupTouched();
      this.snackBar.open('Por favor, preencha todos os campos obrigatórios corretamente.', 'Fechar', { duration: 4000 });
      return;
    }

    const currentCpf = this.userForm.get('cpf')?.value;
    if (!currentCpf || !isValidCPF(currentCpf)) {
      this.snackBar.open('CPF é obrigatório para continuar usando o sistema.', 'Fechar', { duration: 4000 });
      return;
    }

    try {
      const response = await firstValueFrom(
        this.userPreferencesCacheService.updateUser(this.userForm.value.id, this.userForm.value)
      );

      this.snackBar.open('Usuário salvo com sucesso.', 'Fechar', { duration: 4000 });
      
      // Atualizar estado com novos dados
      this.user = response;
      this.originalCpf = response.cpf || null;
      this.isCpfRequired = false;
      this.isEditMode = false;
      this.updateFormState();

    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Erro ao salvar usuário.';
      this.snackBar.open(msg, 'Fechar', { duration: 6000 });
    }
  }

  validateCPF(control: any) {
    const cpf = control.value;
    if (cpf && !isValidCPF(cpf)) {
      return { invalidCPF: true };
    }
    return null;
  }

  logout(): void {
    this.authCustomService.logout();
  }

  private markFormGroupTouched(): void {
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
    });
  }

  openTermsDialog(): void {
    this.dialog.open(DialogTermsComponent, {
      width: '90vw',
      maxWidth: '800px',
      height: '80%',
      maxHeight: '800px',
      panelClass: 'dialog-responsive'
    });
  }

  openPrivacyPolicyDialog(): void {
    this.dialog.open(DialogPrivacyPolicyComponent, {
      width: '90vw',
      maxWidth: '800px',
      height: '80%',
      maxHeight: '800px',
      panelClass: 'dialog-responsive'
    });
  }
}
